import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CATEGORY_LIST } from "@/lib/leadMeta";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Loader2, FileSpreadsheet, AlertTriangle, Copy, History, ChevronDown } from "lucide-react";

const TEMPLATE = "name,company,email,phone,address,city,state,zip,insurance_type,status,premium_estimate,notes\n" +
  "Jane Doe,Acme Corp,jane@example.com,(555) 123-4567,\"12 Main St, Apt 4\",Austin,TX,78701,life,new,1800,Interested in term life\n" +
  "Bob Lee,,bob@example.com,(555) 987-6543,9 Oak Ave,Denver,CO,80202,health,contacted,650,Family plan";

const FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "company", label: "Company" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP" },
  { key: "insurance_type", label: "Insurance type" },
  { key: "status", label: "Status" },
  { key: "premium_estimate", label: "Premium" },
  { key: "notes", label: "Notes" },
];

const SYNONYMS = {
  name: ["name", "full name", "fullname", "lead name", "contact", "contact name", "client", "insured", "insured name"],
  company: ["company", "business", "organization", "organisation", "org", "employer"],
  email: ["email", "e-mail", "email address", "mail"],
  phone: ["phone", "telephone", "mobile", "cell", "phone number", "tel", "cell phone"],
  address: ["address", "street", "street address", "address1", "address 1", "address line 1", "addr"],
  city: ["city", "town"],
  state: ["state", "st", "province", "region"],
  zip: ["zip", "zipcode", "zip code", "postal", "postal code", "postcode"],
  insurance_type: ["insurance_type", "insurance type", "coverage", "product", "type", "line", "coverage type"],
  status: ["status", "stage", "lead status", "pipeline"],
  premium_estimate: ["premium_estimate", "premium", "annual premium", "premium estimate", "premium ($)", "premium/yr"],
  notes: ["notes", "note", "comments", "remarks", "description"],
};

const normPhone = (p) => (p || "").replace(/\D/g, "");

function parseCSVLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQ = true;
    } else if (ch === ",") {
      out.push(cur); cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

function parseCSV(text) {
  const clean = text.replace(/^\uFEFF/, "");
  const lines = clean.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseCSVLine(lines[0]).map((h) => h.trim());
  const rows = lines.slice(1).map((line) => parseCSVLine(line));
  return { headers, rows };
}

function autoMap(headers) {
  const norm = headers.map((h) => h.trim().toLowerCase());
  const map = {};
  FIELDS.forEach(({ key }) => {
    const syns = SYNONYMS[key] || [key];
    map[key] = norm.findIndex((h) => syns.includes(h));
  });
  return map;
}

function buildLeads(rows, mapping) {
  return rows.map((cells) => {
    const g = (f) => { const i = mapping[f]; return i != null && i >= 0 ? (cells[i] || "").trim() : ""; };
    const it = g("insurance_type").toLowerCase();
    const prem = g("premium_estimate").replace(/[^0-9.]/g, "");
    return {
      name: g("name"), company: g("company"), email: g("email"), phone: g("phone"),
      address: g("address"), city: g("city"), state: g("state"), zip: g("zip"),
      insurance_type: CATEGORY_LIST.includes(it) ? it : "life",
      status: g("status") || "new",
      premium_estimate: prem ? Number(prem) : null,
      notes: g("notes"),
    };
  }).filter((r) => r.name);
}

const mapKey = (headers) => "leadImportMap:" + headers.map((h) => h.trim().toLowerCase()).join("|");
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export const BulkImportDialog = ({ onDone }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [usedSaved, setUsedSaved] = useState(false);
  const [dupIndex, setDupIndex] = useState({ phones: new Set(), emails: new Set() });
  const [rowActions, setRowActions] = useState({}); // index -> 'skip' | 'merge' | 'new'
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchHistory = useCallback(() => {
    api.get("/leads/import-history").then(({ data }) => setHistory(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/leads/contact-index")
      .then(({ data }) => setDupIndex({ phones: new Set(data.phones || []), emails: new Set(data.emails || []) }))
      .catch(() => {});
    fetchHistory();
  }, [open, fetchHistory]);

  const reset = () => { setHeaders([]); setRawRows([]); setMapping({}); setFileName(""); setUsedSaved(false); setRowActions({}); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: h, rows } = parseCSV(ev.target.result);
      if (!h.length || !rows.length) { toast.error("No rows found in CSV"); reset(); return; }
      setHeaders(h); setRawRows(rows); setRowActions({});
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(mapKey(h))); } catch { /* ignore */ }
      setMapping(saved || autoMap(h));
      setUsedSaved(!!saved);
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    downloadBlob(TEMPLATE, "ishareleads_leads_template.csv", "text/csv");
  };

  const updateMap = (key, v) => { setMapping((m) => ({ ...m, [key]: Number(v) })); setUsedSaved(false); };
  const resetAuto = () => { setMapping(autoMap(headers)); setUsedSaved(false); };

  const isDup = (r) => (r.phone && dupIndex.phones.has(normPhone(r.phone))) || (r.email && dupIndex.emails.has(r.email.toLowerCase()));
  const parsed = headers.length ? buildLeads(rawRows, mapping).map((r, i) => ({ ...r, _i: i, _dup: isDup(r) })) : [];
  const dupCount = parsed.filter((r) => r._dup).length;
  const actionFor = (r) => (r._dup ? (rowActions[r._i] || "skip") : "new");
  const createLeads = parsed.filter((r) => actionFor(r) === "new");
  const mergeLeads = parsed.filter((r) => actionFor(r) === "merge");
  const skipCount = parsed.filter((r) => actionFor(r) === "skip").length;
  const totalImport = createLeads.length + mergeLeads.length;
  const nameMapped = mapping.name != null && mapping.name >= 0;
  const skipped = rawRows.length - parsed.length;

  const applyAllDups = (action) => {
    setRowActions((prev) => {
      const next = { ...prev };
      parsed.forEach((r) => { if (r._dup) next[r._i] = action; });
      return next;
    });
  };

  const strip = ({ _i, _dup, ...rest }) => rest; // eslint-disable-line no-unused-vars

  const submit = async () => {
    if (!nameMapped) return toast.error("Map the Name column first");
    if (!totalImport) return toast.error("Nothing to import — all rows are set to skip");
    setLoading(true);
    try {
      const { data } = await api.post("/leads/import", {
        file_name: fileName,
        create: createLeads.map(strip),
        merge: mergeLeads.map(strip),
        skipped: skipCount,
      });
      try { localStorage.setItem(mapKey(headers), JSON.stringify(mapping)); } catch { /* ignore */ }
      const bits = [];
      if (data.created) bits.push(`${data.created} added`);
      if (data.merged) bits.push(`${data.merged} merged`);
      if (data.skipped) bits.push(`${data.skipped} skipped`);
      toast.success(bits.length ? `Import complete · ${bits.join(" · ")}` : "Import complete");
      reset(); setOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-import-button"><Upload className="mr-2 h-4 w-4" /> Import CSV</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle className="font-heading">Bulk import leads</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={downloadTemplate} data-testid="download-template-button" className="text-primary">
              <Download className="mr-2 h-4 w-4" /> Download CSV template
            </Button>
            {history.length > 0 && (
              <button onClick={() => setShowHistory((s) => !s)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="import-history-toggle">
                <History className="h-3.5 w-3.5" /> Import history ({history.length}) <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showHistory ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>

          {showHistory && history.length > 0 && (
            <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-border p-3" data-testid="import-history-list">
              {history.map((h) => (
                <button key={h.import_id} type="button"
                  onClick={() => { if (h.created > 0) { setOpen(false); navigate(`/app/leads?import=${h.import_id}&label=${encodeURIComponent(h.file_name)}`); } }}
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${h.created > 0 ? "cursor-pointer hover:bg-accent" : "cursor-default"}`}
                  data-testid={`import-history-${h.import_id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{h.file_name}</p>
                    <p className="text-muted-foreground">{h.user_name} · {fmtDate(h.ts)}</p>
                  </div>
                  <span className="shrink-0 text-muted-foreground">{h.created} added{h.merged ? ` · ${h.merged} merged` : ""}{h.skipped ? ` · ${h.skipped} skipped` : ""}</span>
                </button>
              ))}
            </div>
          )}

          {!headers.length ? (
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-10 transition-colors hover:border-primary/50" data-testid="csv-dropzone">
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{fileName || "Click to choose a .csv file"}</span>
              <input type="file" accept=".csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
            </label>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm text-muted-foreground"><FileSpreadsheet className="h-4 w-4" /> {fileName} · {rawRows.length} row{rawRows.length !== 1 ? "s" : ""}</p>
                <Button variant="ghost" size="sm" onClick={reset} data-testid="csv-choose-another">Choose another file</Button>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-medium uppercase text-muted-foreground">Match your columns</p>
                  <button onClick={resetAuto} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="reset-auto-map">Reset to auto</button>
                </div>
                {usedSaved && <p className="mb-2 text-xs text-primary" data-testid="saved-mapping-note">✓ Applied your saved mapping for this file layout.</p>}
                <div className="grid gap-2 sm:grid-cols-2" data-testid="column-mapping">
                  {FIELDS.map(({ key, label, required }) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="w-28 shrink-0 text-sm">{label}{required && <span className="text-destructive"> *</span>}</span>
                      <Select value={String(mapping[key] ?? -1)} onValueChange={(v) => updateMap(key, v)}>
                        <SelectTrigger className="h-9 flex-1" data-testid={`map-${key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="-1">— Not mapped —</SelectItem>
                          {headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `Column ${i + 1}`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                {!nameMapped && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive" data-testid="name-unmapped-warning"><AlertTriangle className="h-3.5 w-3.5" /> Map the Name column to continue.</p>
                )}
              </div>

              {dupCount > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm" data-testid="dupes-bar">
                  <span className="flex items-center gap-2"><Copy className="h-4 w-4 text-amber-600 dark:text-amber-500" /> <strong>{dupCount}</strong> possible duplicate{dupCount !== 1 ? "s" : ""} (match existing phone/email)</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Set all to</span>
                    <Select onValueChange={applyAllDups}>
                      <SelectTrigger className="h-8 w-28" data-testid="dupes-apply-all"><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">Skip</SelectItem>
                        <SelectItem value="merge">Merge</SelectItem>
                        <SelectItem value="new">Import new</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">Preview {parsed.length > 0 ? `· ${parsed.length} row${parsed.length !== 1 ? "s" : ""}` : ""}{skipped > 0 ? ` · ${skipped} skipped (no name)` : ""}</p>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-border" data-testid="import-preview">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                      <tr className="text-left text-xs uppercase text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Name</th>
                        <th className="px-3 py-2 font-medium">Address</th>
                        <th className="px-3 py-2 font-medium">City / State</th>
                        {dupCount > 0 && <th className="px-3 py-2 font-medium">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.length === 0 ? (
                        <tr><td colSpan={dupCount > 0 ? 4 : 3} className="px-3 py-6 text-center text-muted-foreground">No rows with a name to import.</td></tr>
                      ) : parsed.slice(0, 200).map((r) => (
                        <tr key={r._i} className={`border-t border-border/60 ${r._dup ? "bg-amber-500/10" : ""}`} data-testid={`import-preview-row-${r._i}`}>
                          <td className="px-3 py-2 font-medium text-foreground">
                            {r.name}
                            {r._dup && <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-500/50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-500" data-testid={`dup-flag-${r._i}`}><Copy className="h-2.5 w-2.5" /> duplicate</span>}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{r.address || <span className="italic opacity-60">—</span>}</td>
                          <td className="px-3 py-2 text-muted-foreground">{[r.city, r.state].filter(Boolean).join(", ") || <span className="italic opacity-60">—</span>}</td>
                          {dupCount > 0 && (
                            <td className="px-3 py-2">
                              {r._dup ? (
                                <Select value={actionFor(r)} onValueChange={(v) => setRowActions((p) => ({ ...p, [r._i]: v }))}>
                                  <SelectTrigger className="h-8 w-[110px]" data-testid={`dup-action-${r._i}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="skip">Skip</SelectItem>
                                    <SelectItem value="merge">Merge</SelectItem>
                                    <SelectItem value="new">Import new</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : <span className="text-xs text-muted-foreground">New</span>}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsed.length > 200 && <p className="mt-1 text-xs text-muted-foreground">Showing first 200 of {parsed.length}. All will be imported.</p>}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading || !totalImport || !nameMapped} data-testid="confirm-import-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Import {totalImport || ""}{mergeLeads.length ? ` (${mergeLeads.length} merge)` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
